import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Link,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { ReactNode, useEffect, useState } from 'react';
import {
  hasSignedRecordValue,
  SignedRecordSectionModel,
} from '../../domain/signedControlRecord/recordDetailAdapter';
import {
  reportsApi,
  SignedRecordJsonObject,
  SignedRecordJsonValue,
  SignedSafetyControlRecord,
} from '../../services/reportsApi';
import { formatSignedEvidenceDisposition } from '../../domain/acceptance/signedEvidenceDisposition';
import { presentSignedEvidenceFunnel } from '../../domain/acceptance/signedEvidenceFunnel';
import { partitionComparisonRequirements } from '../../domain/acceptance/signedComparisonRequirements';
import { formatFunnelCount } from '../../domain/acceptance/safetyScanProductPresentation';
import { resolveConfiguredCameraLabel, resolveConfiguredZoneLabel } from '../../domain/customerLabels/customerFacingControlLabel';
import { formatSiteDateTime } from '../../domain/siteTime/siteTime';
import { useSiteTimeZone } from '../../hooks/useSiteTimeZone';

export function SignedRecordSectionNav({ sections }: { sections: SignedRecordSectionModel[] }) {
  return (
    <Box
      component="nav"
      aria-label="Signed record sections"
      sx={{
        position: { md: 'sticky' },
        top: { md: 12 },
        zIndex: 2,
        px: { xs: 1.25, md: 2 },
        py: 1.25,
        bgcolor: 'rgba(248,251,255,0.96)',
        borderTop: '1px solid rgba(8,27,51,0.18)',
        borderBottom: '1px solid rgba(8,27,51,0.18)',
      }}
    >
      <Stack
        direction="row"
        spacing={0.5}
        flexWrap={{ xs: 'nowrap', md: 'wrap' }}
        useFlexGap
        sx={{ overflowX: { xs: 'auto', md: 'visible' }, pb: { xs: 0.5, md: 0 } }}
      >
        {sections.filter((section) => section.available).map((section) => (
          <Link
            key={section.id}
            href={`#${section.id}`}
            underline="hover"
            color="primary"
            sx={{
              px: 1,
              py: 0.75,
              borderRadius: 1,
              fontSize: 14,
              lineHeight: 1.2,
              whiteSpace: 'nowrap',
              '&:focus-visible': {
                outline: '3px solid',
                outlineColor: 'primary.main',
                outlineOffset: 2,
              },
            }}
          >
            {section.title}
          </Link>
        ))}
      </Stack>
    </Box>
  );
}

function text(value: SignedRecordJsonValue | undefined, fallback = 'Not retained'): string {
  if (typeof value === 'string' && value.trim()) return value;
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return fallback;
}

function formatDate(value: SignedRecordJsonValue | undefined, timeZone?: string | null): string {
  if (typeof value !== 'string' || !value) return 'Not retained';
  return formatSiteDateTime(value, timeZone) || value;
}

function Metric({
  label,
  value,
}: {
  label: string;
  value: SignedRecordJsonValue | undefined;
}) {
  return (
    <Paper variant="outlined" sx={{ p: 1.5, minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="h6" sx={{ mt: 0.25, overflowWrap: 'anywhere' }}>
        {text(value)}
      </Typography>
    </Paper>
  );
}

function Section({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: ReactNode;
}) {
  return (
    <Box
      component="section"
      id={id}
      aria-labelledby={`${id}-heading`}
      sx={{ py: { xs: 2.5, md: 3.5 }, scrollMarginTop: 96, borderTop: 1, borderColor: 'divider' }}
    >
      <Typography id={`${id}-heading`} variant="h4" component="h2" sx={{ mb: 2 }}>
        {title}
      </Typography>
      {children}
    </Box>
  );
}

function DetailValue({ value }: { value: SignedRecordJsonValue }) {
  if (value == null || typeof value !== 'object') {
    return <Typography component="span">{text(value)}</Typography>;
  }
  return (
    <Box component="pre" sx={{ m: 0, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', fontSize: 13 }}>
      {JSON.stringify(value, null, 2)}
    </Box>
  );
}

function EvidenceSection({
  record,
  siteId,
}: {
  record: SignedSafetyControlRecord;
  siteId?: string | null;
}) {
  const evidence = record.signed_body.evidence.slice(0, 3);
  const timeZone = useSiteTimeZone(siteId);
  const [media, setMedia] = useState<{ title: string; kind: 'snapshot' | 'clip'; url: string } | null>(null);
  const [thumbs, setThumbs] = useState<Record<string, string>>({});
  const [error, setError] = useState('');

  useEffect(() => () => {
    if (media?.url) URL.revokeObjectURL(media.url);
  }, [media]);

  const snapshotKey = evidence.map((item) => `${item?.evidence_id}:${item?.snapshot_ref || ''}`).join('|');
  useEffect(() => {
    let cancelled = false;
    const objectUrls: string[] = [];
    evidence.forEach((item) => {
      const ref = item?.snapshot_ref;
      if (!item?.evidence_id || !ref) return;
      reportsApi.fetchSignedSafetyControlRecordEvidenceMedia(ref).then((blob) => {
        if (cancelled) return;
        const url = URL.createObjectURL(blob);
        objectUrls.push(url);
        setThumbs((current) => ({ ...current, [item.evidence_id]: url }));
      }).catch(() => undefined);
    });
    return () => {
      cancelled = true;
      objectUrls.forEach((url) => URL.revokeObjectURL(url));
    };
    // snapshotKey captures evidence id + snapshot_ref identity for this revision.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapshotKey]);

  const openMedia = async (
    title: string,
    kind: 'snapshot' | 'clip',
    ref: string | null,
  ) => {
    if (!ref) return;
    try {
      setError('');
      const blob = await reportsApi.fetchSignedSafetyControlRecordEvidenceMedia(ref);
      setMedia((current) => {
        if (current?.url) URL.revokeObjectURL(current.url);
        return { title, kind, url: URL.createObjectURL(blob) };
      });
    } catch {
      setError('Media is unavailable. The signed evidence facts remain unchanged.');
    }
  };

  return (
    <Section id="evidence" title="Evidence">
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        Three representative slots from this Signed revision. Media availability never alters
        signed facts.
      </Typography>
      {error ? <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert> : null}
      <Box
        sx={{
          display: 'grid',
          gap: 1.5,
          gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
        }}
      >
        {Array.from({ length: 3 }, (_, index) => {
          const item = evidence[index];
          const metadata = (item?.metadata || {}) as SignedRecordJsonObject;
          const thumb = item?.evidence_id ? thumbs[item.evidence_id] : undefined;
          return (
            <Paper
              key={item?.evidence_id || `empty-${index}`}
              variant="outlined"
              data-testid={`detail-evidence-slot-${index + 1}`}
              sx={{ p: 2, minWidth: 0 }}
            >
              {item ? (
                <Stack spacing={1.25}>
                  <Stack direction="row" spacing={1} justifyContent="space-between">
                    <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                      {item.evidence_id}
                    </Typography>
                    {item.illustrative ? <Chip size="small" label="Illustrative" /> : null}
                  </Stack>
                  {thumb ? (
                    <Box
                      component="img"
                      src={thumb}
                      alt={`${item.evidence_id} signed snapshot`}
                      sx={{
                        width: '100%',
                        aspectRatio: '16 / 10',
                        objectFit: 'cover',
                        bgcolor: '#dce6ed',
                        borderTop: '3px solid #18bde3',
                      }}
                    />
                  ) : null}
                  <Typography variant="subtitle1" fontWeight={800}>
                    {item.title || 'Representative evidence'}
                  </Typography>
                  <Typography variant="body2">Timestamp · {formatDate(metadata.timestamp, timeZone)}</Typography>
                  <Typography variant="body2">
                    Camera · {resolveConfiguredCameraLabel(text(metadata.camera_id ?? metadata.camera, '')) || text(metadata.camera_id ?? metadata.camera)}
                  </Typography>
                  <Typography variant="body2">
                    Zone · {resolveConfiguredZoneLabel(text(metadata.zone_id ?? metadata.zone, '')) || text(metadata.zone_id ?? metadata.zone)}
                  </Typography>
                  <Typography variant="body2" data-testid="signed-evidence-disposition">
                    Review · {formatSignedEvidenceDisposition(text(metadata.review_status, ''))}
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={!item.snapshot_ref}
                      onClick={() => void openMedia(item.evidence_id, 'snapshot', item.snapshot_ref)}
                    >
                      Open snapshot
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={!item.clip_ref}
                      onClick={() => void openMedia(item.evidence_id, 'clip', item.clip_ref)}
                    >
                      Play clip
                    </Button>
                  </Stack>
                </Stack>
              ) : (
                <Typography color="text.secondary">No evidence retained in this slot.</Typography>
              )}
            </Paper>
          );
        })}
      </Box>
      <Dialog open={Boolean(media)} onClose={() => setMedia(null)} maxWidth="md" fullWidth>
        <DialogTitle>{media?.title} · {media?.kind}</DialogTitle>
        <DialogContent>
          {media?.kind === 'snapshot' ? (
            <Box component="img" src={media.url} alt={`${media.title} signed snapshot`} sx={{ width: '100%' }} />
          ) : null}
          {media?.kind === 'clip' ? (
            <Box component="video" src={media.url} controls aria-label={`${media.title} signed clip`} sx={{ width: '100%' }} />
          ) : null}
        </DialogContent>
      </Dialog>
    </Section>
  );
}

export function SignedRecordSections({
  sections: _sections,
  record,
  orgId: _orgId,
  siteId,
}: {
  sections: SignedRecordSectionModel[];
  record: SignedSafetyControlRecord;
  orgId?: string | null;
  siteId?: string | null;
}) {
  const {
    identity,
    governance,
    scope,
    finding,
    control,
    recheck,
    result,
    next_decision: nextDecision,
    technical_annex: annex,
  } = record.signed_body;
  const annexAvailable = annex.available && annex.source_revision_matches;
  const timeZone = useSiteTimeZone(siteId);
  const signedFunnel = presentSignedEvidenceFunnel(
    record.signed_body.observation_funnel as Record<string, unknown> | undefined,
  );
  const comparisonSplit = partitionComparisonRequirements(recheck.comparability_checklist);
  const decision = typeof nextDecision === 'object' && nextDecision && !Array.isArray(nextDecision)
    ? nextDecision.decision
    : nextDecision;
  const annexGroups: Array<[string, SignedRecordJsonValue | undefined]> = [
    ['Measurement assurance', annex.measurement_assurance],
    ['Convergence', annex.convergence],
    ['Coverage', annex.coverage],
    ['Scan review', annex.scan_review],
    ['Walkdown', annex.walkdown],
    ['Provenance', annex.provenance],
  ];
  const retainedAnnexGroups = annexGroups.filter(
    (entry): entry is [string, SignedRecordJsonValue] => hasSignedRecordValue(entry[1]),
  );

  return (
    <Stack spacing={{ xs: 2.5, md: 4 }} aria-label="Signed record report sections">
      <Section id="finding" title="Recurring pattern">
        <Stack spacing={2}>
          <Typography>{text(finding.summary)}</Typography>
          {finding.operational_observation
            && finding.reviewed_episodes == null
            && finding.rate_per_usable_hour == null ? (
            <Box
              data-testid="operational-observation"
              sx={{
                p: 1.5,
                border: '1px solid rgba(8,27,51,0.18)',
                borderRadius: 1,
                bgcolor: 'rgba(248,251,255,0.8)',
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 800, letterSpacing: '0.08em' }}>
                {(finding.operational_observation.label
                  || 'Operational observation · Not a claimable rate').toUpperCase()}
              </Typography>
              <Box
                sx={{
                  mt: 1.25,
                  display: 'grid',
                  gap: 1.5,
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' },
                }}
              >
                <Metric
                  label="Confirmed episodes (operational)"
                  value={finding.operational_observation.confirmed_episodes ?? null}
                />
                <Metric
                  label="Monitored hours (untrusted)"
                  value={finding.operational_observation.monitored_hours ?? null}
                />
                <Metric
                  label="Candidate signals (operational)"
                  value={finding.operational_observation.candidate_episodes ?? null}
                />
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                These counts are retained for operational context only. They are not reviewed
                episodes per usable hour and do not support a claimable rate.
                {finding.operational_observation.denominator_trust
                  ? ` Denominator trust · ${finding.operational_observation.denominator_trust}.`
                  : ''}
              </Typography>
            </Box>
          ) : null}
          {finding.reviewed_episodes == null
            && finding.usable_hours == null
            && finding.rate_per_usable_hour == null ? (
            <Alert severity="info" data-testid="claimable-metrics-withheld">
              Claimable reviewed-episode rates were not retained on this signed revision because
              the measurement denominator is not trusted.
              {finding.coverage_percent != null
                ? ` Observation coverage retained · ${finding.coverage_percent}%.`
                : ''}
            </Alert>
          ) : (
            <>
              <Box
                sx={{
                  display: 'grid',
                  gap: 1.5,
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' },
                }}
              >
                <Metric label="Reviewed episodes" value={finding.reviewed_episodes} />
                <Metric label="Usable hours" value={finding.usable_hours} />
                <Metric
                  label="Rate per usable hour"
                  value={finding.rate_per_usable_hour?.toFixed(2) ?? null}
                />
              </Box>
              {finding.coverage_percent != null ? (
                <Chip
                  size="small"
                  variant="outlined"
                  label={`Observation coverage ${finding.coverage_percent}%`}
                  sx={{ alignSelf: 'flex-start' }}
                />
              ) : null}
            </>
          )}
          <Typography variant="body2" color="text.secondary">
            Signed window · {scope.observation_window_label || 'Not retained'}
          </Typography>
          {signedFunnel.retained ? (
            <Typography variant="body2" data-testid="signed-evidence-funnel">
              {`Evidence review · ${formatFunnelCount(signedFunnel.funnel.reviewed)} reviewed · ${formatFunnelCount(signedFunnel.funnel.confirmed)} confirmed · ${formatFunnelCount(signedFunnel.funnel.falsePositive)} false positives · ${formatFunnelCount(signedFunnel.funnel.duplicate)} duplicates · ${formatFunnelCount(signedFunnel.funnel.needsMoreReview)} needing further review`}
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary" data-testid="signed-evidence-funnel-missing">
              {signedFunnel.notRetainedMessage}
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary">
            Peak location and time concentration are shown in the visual story above.
          </Typography>
        </Stack>
      </Section>

      <Section id="control" title="Control">
        <Stack spacing={2}>
          <Typography variant="h6">{control.description || 'Not retained'}</Typography>
          <Box
            sx={{
              display: 'grid',
              gap: 1.5,
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' },
            }}
          >
            <Metric label="Status" value={control.status} />
            <Metric label="Owner" value={control.owner} />
            <Metric label="Implemented" value={formatDate(control.implemented_at, timeZone)} />
          </Box>
          {control.location ? <Typography>Exact location · {control.location}</Typography> : null}
          {control.verification_method ? (
            <Typography>Verification method · {control.verification_method}</Typography>
          ) : null}
          {control.field_verifier ? (
            <Typography>
              Field verifier · {control.field_verifier} · {formatDate(control.verified_at, timeZone)}
            </Typography>
          ) : null}
          {control.implementation_evidence_reference ? (
            <Typography>
              Implementation evidence · {control.implementation_evidence_reference}
            </Typography>
          ) : null}
          {control.supporting_control ? (
            <Typography>Supporting control · {control.supporting_control}</Typography>
          ) : null}
          {control.notes ? <Typography>Notes · {control.notes}</Typography> : null}
        </Stack>
      </Section>

      <Section id="recheck" title="Comparable recheck">
        <Stack spacing={2}>
          <Typography>{recheck.summary || 'No comparable recheck summary retained.'}</Typography>
          <Box
            sx={{
              display: 'grid',
              gap: 1.5,
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' },
            }}
          >
            <Paper variant="outlined" sx={{ p: 1.5, minWidth: 0, gridColumn: { sm: '1 / -1' }, bgcolor: 'rgba(248,251,255,0.9)' }}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                justifyContent="space-between"
                alignItems={{ sm: 'flex-end' }}
              >
                <Box>
                  <Typography variant="caption" color="text.secondary">Primary rates</Typography>
                  <Typography variant="h5" sx={{ mt: 0.25, fontWeight: 800, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>
                    {finding.rate_per_usable_hour == null ? '—' : `${finding.rate_per_usable_hour.toFixed(2)}/h`}
                    {' → '}
                    {recheck.rate_per_usable_hour == null ? '—' : `${recheck.rate_per_usable_hour.toFixed(2)}/h`}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Baseline → recheck · absolute delta{' '}
                    {result.absolute_rate_change == null
                      ? 'not retained'
                      : `${result.absolute_rate_change.toFixed(2)}/h`}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  {finding.coverage_percent != null ? (
                    <Chip size="small" variant="outlined" label={`Baseline coverage ${finding.coverage_percent}%`} />
                  ) : null}
                  {recheck.coverage_percent != null ? (
                    <Chip size="small" variant="outlined" label={`Recheck coverage ${recheck.coverage_percent}%`} />
                  ) : null}
                </Stack>
              </Stack>
            </Paper>
            <Metric label="Reviewed episodes" value={recheck.reviewed_episodes} />
            <Metric label="Usable hours" value={recheck.usable_hours} />
            <Metric
              label="Rate per usable hour"
              value={recheck.rate_per_usable_hour?.toFixed(2) ?? null}
            />
          </Box>
          <Typography variant="body2">
            Recheck window · {recheck.window || 'Not retained'} · {formatDate(recheck.start, timeZone)} –{' '}
            {formatDate(recheck.end, timeZone)}
          </Typography>
          <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
            {recheck.comparable ? 'Comparison requirements met' : 'Comparison requirements not met'}
          </Typography>
          {comparisonSplit.failed.length ? (
            <Alert severity="warning" data-testid="signed-comparison-failures">
              Failed requirements: {comparisonSplit.failed.map((item) => item.label).join('; ')}
            </Alert>
          ) : null}
          <Box
            component="ul"
            aria-label="Signed comparability checklist"
            sx={{
              m: 0,
              p: 0,
              listStyle: 'none',
              display: 'flex',
              flexWrap: 'wrap',
              gap: 0.75,
            }}
          >
            {recheck.comparability_checklist.map((item) => {
              const coverageItem = /coverage/i.test(item.label);
              return (
                <Box component="li" key={item.label}>
                  <Chip
                    size="small"
                    variant={coverageItem ? 'outlined' : 'filled'}
                    color={item.met ? (coverageItem ? 'default' : 'success') : 'error'}
                    label={`${item.met ? '✓' : 'Failed'} ${item.label}`}
                    sx={coverageItem ? { bgcolor: 'transparent' } : { bgcolor: item.met ? 'rgba(8,127,165,0.12)' : undefined }}
                  />
                </Box>
              );
            })}
          </Box>
          <Typography variant="body2" color="text.secondary">
            Measurement denominator {recheck.measurement_denominator_verified ? 'verified' : 'not verified'}
          </Typography>
        </Stack>
      </Section>

      <EvidenceSection record={record} siteId={siteId} />

      <Section id="operating-decision" title="Operating decision">
        <Typography variant="h6">{text(decision, 'No next decision retained')}</Typography>
      </Section>

      <Section id="governance" title="Identity & governance">
        <Box
          component="dl"
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'minmax(160px, .35fr) minmax(0, 1fr)' },
            gap: 1.25,
            m: 0,
          }}
        >
          {[
            ['Signed revision', identity.revision],
            ['Lifecycle', identity.lifecycle_state === 'superseded' ? 'Superseded' : 'Signed'],
            ['Provenance', identity.provenance_label],
            ['Signer', governance.signer_state !== 'legacy_unavailable'
              && governance.signer_name && governance.signer_role
              ? `${governance.signer_name} · ${governance.signer_role}`
              : governance.signer_label],
            ['Signed date', governance.signed_at],
            ['Organization', scope.organization],
            ['Site', scope.site],
            ['Zone', scope.zone],
            ['Camera', scope.camera],
            ['Policy', scope.policy],
          ].map(([label, value]) => (
            <Box key={String(label)} sx={{ display: 'contents' }}>
              <Typography component="dt" color="text.secondary">{String(label)}</Typography>
              <Typography component="dd" sx={{ m: 0, overflowWrap: 'anywhere' }}>
                {text(value as SignedRecordJsonValue)}
              </Typography>
            </Box>
          ))}
        </Box>
      </Section>

      {annexAvailable ? (
        <Section id="technical-annex" title="Technical annex">
          <Accordion disableGutters elevation={0} defaultExpanded={false}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight={700}>Matching-revision technical detail</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2}>
                {retainedAnnexGroups.map(([label, value]) => (
                  <Box key={label}>
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>{label}</Typography>
                    <DetailValue value={value} />
                  </Box>
                ))}
              </Stack>
            </AccordionDetails>
          </Accordion>
        </Section>
      ) : null}
    </Stack>
  );
}
